import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import DateTimePicker from 'components/DateTimePicker';
import { Input, Select } from 'components/FormFields';
import PhoneInput from 'components/FormFields/PhoneInput';

// Form card container
const FormCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

const FormSection = styled.div`
  margin-bottom: ${({ noMargin }) => (noMargin ? '0' : '24px')};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#0F172A'};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtext = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.text?.secondary || '#64748B'};
  margin: -12px 0 20px 0;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || '1fr 1fr'};
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) =>
    theme?.colors?.text?.secondary ||
    (theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin-bottom: 6px;
`;

// Icons
const BuildingIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M12 6h.01" />
    <path d="M12 10h.01" />
    <path d="M12 14h.01" />
    <path d="M16 10h.01" />
    <path d="M16 14h.01" />
    <path d="M8 10h.01" />
    <path d="M8 14h.01" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

// Platform detection
const isTicketHoss = process.env.REACT_APP_PLATFORM === 'tickethoss';

const PROMOTER_TYPE_OPTIONS = [
  { value: '', label: 'Select type...' },
  { value: 'track', label: 'Track' },
  { value: 'series', label: 'Series' },
  { value: 'independent', label: 'Independent' },
];

const DetailsTab = ({ values, setFieldValue }) => {
  const handleChange = (field) => (e) => {
    setFieldValue(field, e.target.value);
  };

  const handlePhoneChange = (value) => {
    setFieldValue('phone', value ?? '');
  };

  const handleCountryCodeChange = (countryCode) => {
    // countryCode comes as '+1', '+44', etc.
    setFieldValue('country_code', countryCode);
    // Extract just the number part (e.g., '1' from '+1')
    const callingCode = countryCode.replace('+', '');
    setFieldValue('calling_code', callingCode);
  };

  return (
    <>
      {/* Business Details */}
      <FormCard>
        <FormSection>
          <SectionTitle>
            <BuildingIcon />
            Business Details
          </SectionTitle>

          <FormRow>
            <FormGroup>
              <Input
                label="Business DBA"
                value={values.business_dba || ''}
                onChange={handleChange('business_dba')}
                placeholder="Doing business as name"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Legal Name"
                value={values.legal_name || ''}
                onChange={handleChange('legal_name')}
                placeholder="Legal business name"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Input
                label="Primary Contact"
                value={values.primary_contact || ''}
                onChange={handleChange('primary_contact')}
                placeholder="Contact person name"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Title"
                value={values.title || ''}
                onChange={handleChange('title')}
                placeholder="Contact person title"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <PhoneInput
                label="Phone Number"
                value={values.phone || ''}
                onChange={handlePhoneChange}
                countryCode={values.country_code || '+1'}
                onCountryChange={handleCountryCodeChange}
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Primary Email"
                value={values.email || ''}
                onChange={handleChange('email')}
                placeholder="Email address"
                type="email"
              />
            </FormGroup>
          </FormRow>
        </FormSection>
      </FormCard>

      {/* Promoter Information */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <UserIcon />
            Promoter Information
          </SectionTitle>
          <SectionSubtext>
            This information is public-facing and may be displayed to customers
            and fans in the ticketing app.
          </SectionSubtext>

          <FormRow>
            <FormGroup>
              <Input
                label="Name"
                value={values.promoter_name || ''}
                onChange={handleChange('promoter_name')}
                placeholder="Promoter name"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Email"
                value={values.promoter_email || ''}
                onChange={handleChange('promoter_email')}
                placeholder="Email address"
                type="email"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Input
                label="Phone"
                value={values.promoter_phone || ''}
                onChange={handleChange('promoter_phone')}
                placeholder="Phone number"
                type="tel"
              />
            </FormGroup>
            <FormGroup>
              <Input
                label="Website"
                value={values.website || ''}
                onChange={handleChange('website')}
                placeholder="https://example.com"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Input
                label="Organization Name"
                value={values.org_name || ''}
                onChange={handleChange('org_name')}
                placeholder="Organization name"
              />
            </FormGroup>
            <FormGroup>
              <Select
                label="Promoter Type"
                value={values.promoter_type || ''}
                onChange={handleChange('promoter_type')}
                options={PROMOTER_TYPE_OPTIONS}
              />
            </FormGroup>
          </FormRow>
        </FormSection>
      </FormCard>

      {/* Race Times */}
      <FormCard>
        <FormSection noMargin>
          <SectionTitle>
            <ClockIcon />
            Race Times
          </SectionTitle>

          <FormRow>
            <FormGroup>
              <FieldLabel>
                {isTicketHoss ? 'Front Gate Time' : 'Pit Gate Time'}
              </FieldLabel>
              <DateTimePicker
                value={values.gate_time || ''}
                onChange={(time) => setFieldValue('gate_time', time)}
                timeOnly
              />
            </FormGroup>
            <FormGroup>
              <FieldLabel>Race Start Time</FieldLabel>
              <DateTimePicker
                value={values.start_time || ''}
                onChange={(time) => setFieldValue('start_time', time)}
                timeOnly
              />
            </FormGroup>
          </FormRow>
        </FormSection>
      </FormCard>
    </>
  );
};

DetailsTab.propTypes = {
  values: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default DetailsTab;
